<template>
  <v-card
    v-if="!loading"
    :to="disabled || redirect === true ? undefined : route"
    ripple
    raised
    :disabled="disabled || inactive"
    :hover="!disabled"
    height="100%"
    @click="redirectToOutSide"
    :tk-id="trackingIdentifier"
    class="card"
  >
    <v-layout
      class="hub-item-outer-container"
      column
      fill-height
      :class="{ disabled: disabled }"
    >
      <div class="hub-item-text-container scroll-fire-fox">
        <div :style="{'font-size':titleFontSize}" class="hub-item-title">
          {{ title }}
        </div>

        <div class="hub-item-description">
          {{ description }}
        </div>

        <dev-tag
          v-if="dev && isSuperAdmin"
          position="relative"
          style="border-radius: 8px; margin: 15px 0; background: var(--primary-highlight); color: white"
        />
      </div>
    </v-layout>
  </v-card>
</template>

<script>
import DevTag from "@/components/_Atoms/DevTag";
import InactiveTag from "@/components/_Atoms/InactiveTag";
import { checkPermissionsMixin } from "@/mixins/checkPermissionsMixin";
export default {
  name: "HubItem",

  components: {
    DevTag,
    InactiveTag
  },

  mixins: [checkPermissionsMixin],

  props: {
    loading: {
      type: Boolean,
      default: false
    },

    title: {
      type: String,
      required: true
    },

    description: {
      type: String,
    },

    route: {
      type: String,
      required: true
    },

    permissionName: {
      type: String
    },

    backgroundImage: {
      type: String,
    },

    dev: {
      type: Boolean,
      default: false
    },

    inactive: {
      type: Boolean,
      default: false
    },

    redirect: {
      type: Boolean,
      default: false
    },

    trackingIdentifier: {
      type: String,
    },

    itemSize: {
      type: String,
    },
  },

  methods:{
    redirectToOutSide(){
      // Only handle custom redirect logic when redirect flag is true
      if (!this.redirect || this.disabled || this.inactive) {
        return;
      }

      try {
        // If route is an absolute URL, compare hosts
        if (this.route.startsWith("http://") || this.route.startsWith("https://")) {
          const currentHost = (new URL(window.location.href)).host;
          const targetHost = (new URL(this.route)).host;

          if (currentHost !== targetHost) {
            window.open(this.route);
          } else {
            window.location.href = this.route;
          }
        } else {
          // Relative path (e.g. "/dashboard") – always use same-window navigation
          window.location.href = this.route;
        }
      } catch (e) {
        // Fallback to direct navigation if URL parsing fails for any reason
        console.error("Failed to redirect from HubItem:", e);
        window.location.href = this.route;
      }
    }
  },
  computed: {
    disabled() {
      if (this.permissionName) {
        return !this.userHasPermission(this.permissionName);
      } else return false;
    },

    titleFontSize() {
      switch(this.itemSize) {
        case "xlg":
        case "lg":
        case "md":
          return "30px"
        case "m":
          return "25px"
        case "sm":
          return "18px"
      }
    }
  }
};
</script>

<style scoped>
.fadeHubItem-enter {
  opacity: 0;
}

.fadeHubItem-enter-active {
  transition: opacity 1s 0.5s;
}

.hub-item-outer-container {
  border-radius: 16px !important;
}

.hub-item-outer-container:not(.disabled):hover {
  transition: backdrop-filter 0.5s ease;
}

.hub-item-outer-container.disabled {
  transition: backdrop-filter 0.5s ease;
}

.hub-item-text-container {
  padding: 15px 30px;
  color: #231E46;
  text-align: left;
  text-transform: uppercase;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  background-color: transparent;
}

.management-hub-items-container .hub-item-text-container {
  padding: 5% 30px 15px 30px;
}

.hub-item-outer-container.disabled .hub-item-text-container {
  background-color: transparent;
}

.hub-item-outer-container:not(.disabled):hover .hub-item-text-container {
  transition: background-color 0.5s ease;
  background-color: rgba(255, 255, 255, 0.1) !important;
  border-radius: 16px !important;
  height: 100%;
}

.hub-item-title {
  font-size: 24px;
  line-height: 24px;
  font-weight: 900;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.hub-item-description {
  color: #231E46;
  font-family: Lato;
  font-size: 16px;
  font-style: normal;
  font-weight: 400;
  line-height: 20px; /* 100% */
  text-transform: lowercase;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  margin-top: -5px;
}

.xlg >>> .hub-item-title,
.lg >>> .hub-item-title,
.md >>> .hub-item-title {
  font-size: 56px;
  line-height: 56px;
}

.hub-items-container .sm .hub-item-title {
  font-size: 48px;
  line-height: 48px;
}

.management-hub-items-container .hub-item-title {
  font-size: 20px;
  line-height: 20px;
}

.card {
  border-radius: 16px !important;
  background: rgba(255, 255, 255, 0.50) !important;
  box-shadow: 7px 9px 15px 0px rgba(0, 0, 0, 0.15) !important;
}

@media only
screen and (max-width: 1200px),
screen and (max-height: 950px) {
  .xlg >>> .hub-item-title,
  .lg >>> .hub-item-title,
  .md >>> .hub-item-title {
    font-size: 40px;
    line-height: 40px;
  }

  .sm >>> .hub-item-title {
    font-size: 18px;
    line-height: 18px;
  }

  .hub-items-container .sm .hub-item-title {
    font-size: 36px;
    line-height: 36px;
  }
}

@media only
screen and (max-width: 900px),
screen and (max-height: 855px) {
  .xlg >>> .hub-item-title,
  .lg >>> .hub-item-title,
  .md >>> .hub-item-title {
    font-size: 30px;
    line-height: 30px;
  }

  .sm >>> .hub-item-title,
  .management-hub-items-container .md >>> .hub-item-title,
  .management-hub-items-container .lg >>> .hub-item-title,
  .management-hub-items-container .xlg >>> .hub-item-title {
    font-size: 16px;
    line-height: 16px;
  }

  .hub-items-container .sm .hub-item-title {
    font-size: 24px;
    line-height: 24px;
  }
}

@media only screen and (max-width: 750px) {
  .xlg >>> .hub-item-title,
  .lg >>> .hub-item-title,
  .md >>> .hub-item-title {
    font-size: 18px;
    line-height: 18px;
  }

  .hub-items-container .sm .hub-item-title {
    font-size: 18px;
    line-height: 18px;
  }
}

@media only screen and (max-height: 500px) {
  .hub-item-description {
    display: none;
  }
}

.v-card--link:before {
  border-radius: 16px;
}
</style>



// WEBPACK FOOTER //
// src/components/Hub/HubItem.vue